import React, { useEffect } from 'react';

// Extend the Window interface to include the FeaturedArticle property
declare global {
  interface Window {
    FeaturedArticle?: any;
  }
}
import { SearchFacet } from '../../types/search';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SearchDesktopFilters from '../Search/components/SearchDesktopFilters';
import SearchPagination from '../Search/components/SearchPagination';
import ContentListingSearchResultsContainer from './components/ContentListingSearchResultsContainer';
import FeaturedArticleCard from './components/FeaturedArticleCard';
import { useSearchConfig } from '../Search/hooks/useSearchConfig';
import LoadingSpinner from '../utils/LoadingSpinner';
import SearchNoResults from '../Search/components/SearchNoResults';
import { useContentListing } from './hooks/useContentListing';

const ContentListingInner = () => {
  const config = useSearchConfig();

  const mountElement = document.getElementById('content-listing-root');
  const listingType = mountElement?.dataset.listingType ?? '';

  let featuredArticle = undefined;
  const featuredInput = document.getElementById('featured-article-data');
  if (featuredInput && featuredInput instanceof HTMLInputElement && featuredInput.value) {
    featuredArticle = JSON.parse(featuredInput.value);
  }

  const {
    results,
    isLoading,
    isError,
    isSuccess,
    facets,
    pagination,
    activeTabKey,
    toggleFacet,
    toggleFilter,
    handlePageChange,
    isFetching,
  } = useContentListing(config.siteSearchApi, config.paginationDisplayLimit, listingType);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredFacets: SearchFacet[] = facets
    .filter((facet: SearchFacet) => facet.pagetype === listingType || !facet.pagetype)
    .map((facet: SearchFacet) => ({
      ...facet,
      items: facet.items.filter(item => item.pagetype === listingType || !item.pagetype),
    }))
    .filter((facet: SearchFacet) => facet.items.some(item => item.total > 0));

  const filterProps = {
    facets: filteredFacets,
    appliedFacets: filteredFacets,
    onToggleFacet: toggleFacet,
    onFacetItemToggle: toggleFilter,
    labels: {
      applyFilters: config.labels.applyFiltersLabel,
      clearFilters: config.labels.clearFiltersLabel,
      showingLabel: config.labels.showingResultsForLabel,
      filtersLabel: config.labels.filtersLabel,
      closeLabel: config.labels.closeLabel,
      filterByLabel: config.labels.filterByLabel,
      nextPageLabel: config.labels.nextPageLabel,
      prevPageLabel: config.labels.prevPageLabel,
    },
    onClose: () => {},
  };

  // Use API results and pagination only
  const paginatedResults = results;
  const totalPages = pagination.totalPages;
  const currentPage = pagination.currentPage;

  const renderListingResults = () => {
    let content;

    if (isError) {
      content = (
        <SearchNoResults
          noResultsText={config.labels.noResultsText ?? 'An error occurred while fetching content. Please try again.'}
        />
      );
    } else if (isSuccess && (!paginatedResults || paginatedResults.length === 0)) {
      content = <SearchNoResults noResultsText={config.labels.noResultsText ?? 'No content found.'} />;
    } else {
      content = (
        <ContentListingSearchResultsContainer
          results={paginatedResults}
          facets={filteredFacets}
          searchTerm={''}
          lastSearchedTerm={''}
          isLoading={isLoading}
          hasSearched={true}
          noResultsText={config.labels.noResultsText}
          activeTabKey={activeTabKey ?? listingType}
          videoLabel={config.labels.videoLabel}
          viewLabel={config.labels.viewLabel}
          featuredArticle={featuredArticle}
        />
      );
    }

    return (
      <div className="search__results-container content-listing__results-container--grid">
        {content}
        {isFetching && (
          <div className="search__spinner-overlay">
            <LoadingSpinner />
          </div>
        )}
      </div>
    );
  };

  const hasFiltersOrSortOptions = !isLoading && isSuccess && results.length > 0;

  return (
    <div className="search__container">
      {hasFiltersOrSortOptions && (
        <div className="search__filters-sort-container">
          <div className="search__filters-sort-wrapper">
            <SearchDesktopFilters {...filterProps} />
          </div>
        </div>
      )}

      {featuredArticle &&
        (() => {
          const anyFilterSelected = filteredFacets.some(facet => facet.items.some(item => item.isSelected));
          if (!anyFilterSelected) {
            return (
              <div className="content-listing__featured">
                <div className="content-listing__container">
                  <div className="search__featured-article">
                    <FeaturedArticleCard data={featuredArticle} facets={filteredFacets} />
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

      {renderListingResults()}

      {totalPages > 1 && (
        <div className="search__pagination-container">
          <SearchPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            prevPageLabel={config.labels.prevPageLabel}
            nextPageLabel={config.labels.nextPageLabel}
          />
        </div>
      )}
    </div>
  );
};

const ContentListingQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

export { ContentListingQueryClient };

const ContentListing = () => {
  return (
    <QueryClientProvider client={ContentListingQueryClient}>
      <ContentListingInner />
    </QueryClientProvider>
  );
};

export default ContentListing;
